import type { badgeVariants } from '@/components/ui/badge';
import type { CatalogRecallStatus, RecallCounts } from '@/state';
import { isRecallStuck } from '@rock/recalls';
import type { Recall, RecallsState, RecallStatus, RecallVerdictValue } from '@rock/types';
import type { VariantProps } from 'class-variance-authority';

type BadgeVariant = VariantProps<typeof badgeVariants>['variant'];

interface RecallStatusMeta {
    label: string;
    variant: BadgeVariant;
    className?: string;
    /** What the status means for a homeowner, in one line, for the details header. */
    hint: string;
}

export const RECALL_STATUS_META: Record<RecallStatus, RecallStatusMeta> = {
    new: { label: 'New', variant: 'outline', hint: 'Ingested; not yet classified.' },
    classified: {
        label: 'Classified',
        variant: 'outline',
        className: 'text-slate-600',
        hint: 'Typed or untyped; rules not yet extracted. One with topics can be approved from the Topics tab.',
    },
    draft: {
        label: 'Draft',
        variant: 'secondary',
        className: 'bg-amber-100 text-amber-900 border-transparent',
        hint: 'Rules extracted and awaiting your review. Nothing reaches a homeowner yet.',
    },
    approved: {
        label: 'Approved',
        variant: 'secondary',
        className: 'bg-emerald-100 text-emerald-900 border-transparent',
        hint: 'Live: matching units are evaluated and their owners notified; homes following its topics get it under FYI Only.',
    },
    rejected: {
        label: 'Rejected',
        variant: 'outline',
        className: 'text-muted-foreground',
        hint: 'Kept for the record; matches nothing.',
    },
};

export const CATALOG_STATUS_LABELS: Record<CatalogRecallStatus, string> = {
    draft: 'Drafts',
    approved: 'Approved',
};

/** The candidates stalled right now, per `isRecallStuck`. */
export function stalledRecalls(candidates: Recall[]): Recall[] {
    const now = Date.now();
    return candidates.filter((recall) => isRecallStuck(recall, now));
}

/** Drafts plus approved; undefined while either count is unknown. */
export function catalogRecallCount(counts: RecallCounts | undefined): number | undefined {
    return sumCatalog(counts?.byStatus ?? {}, undefined);
}

/** Recalls with matched units under the catalog's status selection (none selected = drafts plus approved). */
export function catalogMatchesCount(
    counts: RecallCounts | undefined,
    status: CatalogRecallStatus | undefined,
): number | undefined {
    return sumCatalog(counts?.withMatches ?? {}, status);
}

function sumCatalog(
    byStatus: Partial<Record<CatalogRecallStatus, number>>,
    status: CatalogRecallStatus | undefined,
): number | undefined {
    if (status) return byStatus[status];
    const { draft, approved } = byStatus;
    return draft === undefined || approved === undefined ? undefined : draft + approved;
}

/** Chip styling per verdict: what the matcher decided for one unit. */
export const RECALL_VERDICT_META: Record<
    RecallVerdictValue | 'fyi',
    { label: string; variant: BadgeVariant; className?: string }
> = {
    fyi: { label: 'FYI', variant: 'secondary', className: 'bg-sky-100 text-sky-900 border-transparent' },
    affected: { label: 'affected', variant: 'destructive' },
    'possibly-affected': {
        label: 'possibly affected',
        variant: 'secondary',
        className: 'bg-amber-100 text-amber-900 border-transparent',
    },
};

/**
 * Untyped recalls that aren't rejected, across every kind (the catalog-gap list, which topic approvals leave
 * standing); undefined until the state doc loads.
 */
export function untypedRecallCount(state: RecallsState | undefined): number | undefined {
    if (!state) return undefined;
    return Object.values(state.untypedKindCounts ?? {}).reduce((sum, count) => sum + count, 0);
}
